import { useMemo } from 'react';
import { IChatConversation } from 'src/@types/chat';
import { useAuthContext } from 'src/auth/useAuthContext';
import { useLivekitRTCState } from 'src/provider/VoiceChannel/livekit/useLivekitRTCState';
import { useVoiceChannelContext } from './VoiceChannelContext';

export const useVoiceChannelsMembers = (
  channel: IChatConversation,
  sorted: boolean = false,
) => {
  const { voiceChannels } = useVoiceChannelContext();

  const { connectionStatus, localParticipant, activeVoiceChannel } =
    useLivekitRTCState();

  const { user } = useAuthContext();

  const isConnecting = useMemo(
    () => connectionStatus === 'connecting',
    [connectionStatus],
  );

  const isChannelActive = useMemo(
    () =>
      activeVoiceChannel &&
      channel &&
      activeVoiceChannel?.channel?._id === channel?._id,
    [activeVoiceChannel, channel],
  );

  const voiceChannel = useMemo(
    () => voiceChannels.find((vc) => vc.channel?._id === channel?._id),
    [voiceChannels, channel],
  );

  const voiceMembers = useMemo(() => {
    if (!voiceChannel?.members) return [];

    // Usar Map para garantir unicidade baseado no _id do usuário
    const membersMap = new Map(
      voiceChannel.members.map((member) => [member.user._id, member]),
    );

    if (isChannelActive && isConnecting && !!user) {
      const inList = membersMap.get(user._id);

      if (!inList) {
        const { muted, deaf } = localParticipant;
        // Se o membro não existe, cria um novo
        membersMap.set(user._id, {
          socketId: '',
          user: user as any,
          muted,
          deaf,
        });
      }
    }

    const list = Array.from(membersMap.values());

    if (!sorted) return list;

    // put the user sharing screen at the top of the list
    return list.sort((a, b) => {
      // Prioridade para 'sharing'
      if (!!a.sharing !== !!b.sharing) {
        return Number(!!b.sharing) - Number(!!a.sharing);
      }

      // Ordem alfabética por nome do usuário
      return a.user?.name?.localeCompare(b.user?.name);
    });
  }, [
    voiceChannel?.members,
    localParticipant,
    isConnecting,
    isChannelActive,
    user,
    sorted,
  ]);

  return {
    voiceChannel,
    voiceMembers,
    isConnecting,
    isChannelActive,
  };
};
